import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface InterestCount {
  item_id: string;
  count: number;
}

// Get interest counts for a list of items (for marketplace display)
export function useItemInterestCounts(itemIds: string[]) {
  return useQuery({
    // Clone before sort — Array.prototype.sort() mutates in place and would
    // silently reorder the caller's memoized array.
    queryKey: ['item-interest-counts', [...itemIds].sort().join(',')],
    queryFn: async () => {
      if (itemIds.length === 0) return {};

      // We need to count interests per item
      // Since we can't aggregate easily with RLS, we'll fetch the raw data
      const { data, error } = await supabase
        .from('item_interests')
        .select('item_id')
        .in('item_id', itemIds)
        .in('status', ['pending', 'accepted', 'countered']);

      if (error) throw error;

      // Count per item
      const counts: Record<string, number> = {};
      for (const interest of data || []) {
        counts[interest.item_id] = (counts[interest.item_id] || 0) + 1;
      }
      return counts;
    },
    enabled: itemIds.length > 0,
    staleTime: 30000, // 30 seconds
  });
}

// Get pending interest count for a single item (for item owner)
export function useItemInterestCount(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-interest-count', itemId],
    queryFn: async () => {
      if (!itemId) return 0;

      const { count, error } = await supabase
        .from('item_interests')
        .select('*', { count: 'exact', head: true })
        .eq('item_id', itemId)
        .in('status', ['pending', 'accepted', 'countered']);

      if (error) return 0;
      return count ?? 0;
    },
    enabled: !!itemId,
    staleTime: 30000,
  });
}
